import React, { useState } from "react";
import { Search, Plus, MessageSquare } from "lucide-react";
import { Input, Badge, EmptyState } from "../../components/ui";

// Skeleton row for initial conversation-list loading
const ConversationSkeleton = ({ count = 6 }) => {
  return (
    <div className="divide-y divide-neutral-100 dark:divide-neutral-800 animate-pulse" aria-busy="true" aria-label="Loading conversations">
      {Array.from({ length: count }).map((_, idx) => (
        <div key={`conv-skeleton-${idx}`} className="flex items-center gap-3 p-3.5">
          <div className="w-11 h-11 rounded-full bg-neutral-200/80 dark:bg-neutral-800 shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex justify-between items-center gap-2">
              <div className="h-3 bg-neutral-200/80 dark:bg-neutral-800 rounded-md w-2/5" />
              <div className="h-2.5 bg-neutral-200/60 dark:bg-neutral-800 rounded-md w-8" />
            </div>
            <div className="h-2.5 bg-neutral-200/60 dark:bg-neutral-800 rounded-md w-3/5" />
          </div>
        </div>
      ))}
    </div>
  );
};

const ChatSidebar = ({
  conversations = [],
  activeConversation,
  onSelectConversation,
  onOpenNewChatModal,
  currentUserId,
  onlineUserIds = [],
  unreadCounts = {},
  loading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [imageErrors, setImageErrors] = useState({});

  const getConversationTitle = (conv) => {
    if (conv.type === "GROUP") return conv.name || "Group Chat";

    const otherParticipant = conv.participants?.find(
      (p) => p.userId?._id?.toString() !== currentUserId?.toString()
    );

    return otherParticipant?.userId?.name || "Direct Message";
  };

  // Helper to extract first and last initials, or first two letters
  const getInitials = (name) => {
    if (!name) return "DM";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Generate a deterministic color based on the string name
  const getAvatarColor = (name) => {
    const colors = [
      "bg-indigo-600 text-white",
      "bg-emerald-600 text-white",
      "bg-amber-600 text-white",
      "bg-rose-600 text-white",
      "bg-sky-600 text-white",
      "bg-violet-600 text-white",
      "bg-teal-600 text-white",
      "bg-orange-600 text-white",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const renderAvatar = (conv) => {
    const title = getConversationTitle(conv);
    let avatarUrl = null;

    if (conv.type === "GROUP") {
      avatarUrl = conv.groupIcon || null;
    } else {
      const otherParticipant = conv.participants?.find(
        (p) => p.userId?._id?.toString() !== currentUserId?.toString()
      );
      avatarUrl =
        otherParticipant?.userId?.picture ||
        otherParticipant?.userId?.profileImage ||
        null;
    }

    const initials = getInitials(title);
    const colorClass = getAvatarColor(title);
    const hasError = imageErrors[conv._id];

    if (avatarUrl && !hasError) {
      return (
        <img
          src={avatarUrl}
          alt={title}
          onError={() =>
            setImageErrors((prev) => ({ ...prev, [conv._id]: true }))
          }
          className="w-11 h-11 rounded-full object-cover border border-neutral-200 dark:border-neutral-800"
        />
      );
    }

    return (
      <div
        className={`w-11 h-11 rounded-full flex items-center justify-center font-semibold text-xs tracking-wider border border-neutral-200/50 ${colorClass}`}
      >
        {initials}
      </div>
    );
  };

  const isOtherUserOnline = (conv) => {
    if (conv.type === "GROUP") return false;
    const otherParticipant = conv.participants?.find(
      (p) => p.userId?._id?.toString() !== currentUserId?.toString()
    );
    const otherId = otherParticipant?.userId?._id?.toString();
    return onlineUserIds.includes(otherId);
  };

  const filteredConversations = conversations.filter((conv) => {
    const title = getConversationTitle(conv).toLowerCase();
    return title.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="w-full lg:w-80 border-r border-neutral-200/60 bg-white dark:bg-neutral-900 flex flex-col h-full shrink-0">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200/60 flex justify-between items-center bg-neutral-50/50">
        <h1 className="text-base sm:text-lg font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">
          Messages
        </h1>
        <button
          onClick={onOpenNewChatModal}
          className="p-2 bg-neutral-900 hover:bg-neutral-800 text-white rounded-full transition shadow-xs flex items-center justify-center"
          title="New Chat / Group"
          type="button"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Search Input */}
      <div className="p-3 border-b border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-900">
        <Input
          type="text"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          leftIcon={<Search className="w-4 h-4 text-neutral-400 dark:text-neutral-500" />}
          className="text-xs"
        />
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-800">
        {loading ? (
          <ConversationSkeleton count={6} />
        ) : filteredConversations.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={MessageSquare}
              title="No conversations found"
              description="Try adjusting your search query or start a new chat."
            />
          </div>
        ) : (
          filteredConversations.map((conv) => {
            const isSelected = activeConversation?._id === conv._id;
            const title = getConversationTitle(conv);
            const isOnline = isOtherUserOnline(conv);
            const unreadCount =
              unreadCounts[conv._id] ||
              unreadCounts[String(conv._id)] ||
              0;

            return (
              <div
                key={conv._id}
                onClick={() => onSelectConversation(conv)}
                className={`flex items-center gap-3 p-3.5 cursor-pointer transition-colors ${
                  isSelected
                    ? "bg-neutral-100/80 border-l-4 border-neutral-900"
                    : "hover:bg-neutral-50/80"
                }`}
              >
                {/* Avatar with Online Badge */}
                <div className="relative shrink-0">
                  {renderAvatar(conv)}
                  {isOnline && (
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-neutral-900 rounded-full"></span>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline gap-1">
                    <h3
                      className={`text-xs truncate ${ unreadCount > 0 ? "font-bold text-neutral-900 dark:text-neutral-100" : "font-semibold text-neutral-800 dark:text-neutral-100" }`}
                    >
                      {title}
                    </h3>
                    {conv.updatedAt && (
                      <span className="text-[10px] text-neutral-400 dark:text-neutral-500 shrink-0">
                        {new Date(conv.updatedAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-1 gap-2">
                    <p
                      className={`text-xs truncate ${
                        unreadCount > 0
                          ? "text-neutral-900 dark:text-neutral-100 font-medium"
                          : "text-neutral-500 dark:text-neutral-400"
                      }`}
                    >
                      {conv.lastMessageId?.content ||
                        (conv.lastMessageId?.file?.url
                          ? "📷 Attachment"
                          : "No messages yet")}
                    </p>
                    {unreadCount > 0 && (
                      <Badge
                        variant="success"
                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ChatSidebar;